import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';
import { baseToPackageQty, packageQtyToBase, inputUnitLabel } from '../../utils/inventoryUnit';

const fmtN = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });

/**
 * #151257 — cùng khuôn Ngưỡng tối thiểu (#232210): chỉ Admin trung tâm sửa được ĐỊNH MỨC RIÊNG
 * theo chi nhánh (`BranchStock.par_level_override`), việc gọi component này phải tự kiểm
 * `user.role === 'ADMIN'` trước (BE cũng chặn lại bằng `RolesGuard`, không tin FE).
 */
export default function BranchParLevelOverrideModal({ show, onHide, branchId, branchName, stock, onSuccess }) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);

  const material = stock?.material;
  const unitLabel = inputUnitLabel(material);
  const defaultParLevelPkg = baseToPackageQty(material?.par_level, material);

  useEffect(() => {
    if (show && stock) {
      setValue(
        stock.par_level_override != null
          ? String(baseToPackageQty(stock.par_level_override, material))
          : '',
      );
    }
  }, [show, stock, material]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (value !== '' && Number(value) < 0) {
      toast.error('Định mức không được âm');
      return;
    }
    try {
      setLoading(true);
      const baseValue = value === '' ? null : packageQtyToBase(Number(value), material);
      await inventoryAPI.setBranchParLevelOverride(branchId, material.id, baseValue);
      toast.success(
        baseValue === null
          ? 'Đã xoá định mức riêng — quay về mức chung'
          : 'Đã lưu định mức riêng cho chi nhánh này',
      );
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu định mức');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    try {
      setLoading(true);
      await inventoryAPI.setBranchParLevelOverride(branchId, material.id, null);
      toast.success('Đã xoá định mức riêng — quay về mức chung');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể xoá định mức riêng');
    } finally {
      setLoading(false);
    }
  };

  if (!stock || !material) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Định mức riêng — {material.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Chi nhánh: <strong>{branchName}</strong>. Để trống ô bên dưới = dùng mức CHUNG cho mọi
            chi nhánh chưa có override riêng.
          </p>
          <Form.Group className="mb-2">
            <Form.Label>Mức chung hiện tại</Form.Label>
            <div className="text-muted">{fmtN(defaultParLevelPkg)} {unitLabel}</div>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Định mức riêng cho chi nhánh này ({unitLabel})</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.001"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={`Để trống = dùng mức chung (${fmtN(defaultParLevelPkg)} ${unitLabel})`}
            />
          </Form.Group>
          {stock.par_level_override != null && (
            <Form.Text className="text-info">
              Chi nhánh này đang dùng mức RIÊNG: {fmtN(baseToPackageQty(stock.par_level_override, material))} {unitLabel}
            </Form.Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          {stock.par_level_override != null && (
            <Button variant="outline-danger" onClick={handleClear} disabled={loading} className="me-auto">
              Xoá override, dùng mức chung
            </Button>
          )}
          <Button variant="secondary" onClick={onHide} disabled={loading}>Hủy</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
